import '../css/AdminSwitches.css';
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import api from "../api/api";

const emptyInvite = {
  first_name: "",
  last_name: "",
  email: "",
  discipline: "",
  billing_mode: "individual",
  share_clients: false,
};
const errorText = (error) => {
  const data = error.response?.data;
  return (
    data?.detail ||
    (data &&
      Object.values(data)
        .flat()
        .find((value) => typeof value === "string")) ||
    "Unable to save. Please try again."
  );
};
const dateText = (value) =>
  value ? new Date(`${value.slice(0, 10)}T12:00:00`).toLocaleDateString() : "—";

export default function ClinicTeam() {
  const [index, setIndex] = useState(null);
  const [clinicId, setClinicId] = useState("");
  const [team, setTeam] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [invite, setInvite] = useState(null);
  const [managerInvite, setManagerInvite] = useState(false);
  const [edit, setEdit] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [managers, setManagers] = useState([]);
  const generation = useRef(0);
  const clinic = index?.clinics.find((item) => String(item.id) === clinicId);

  useEffect(() => {
    let active = true;
    api
      .get("/clinic-team/")
      .then(({ data }) => {
        if (!active) return;
        setIndex(data);
        setClinicId((current) =>
          data.clinics.some((c) => String(c.id) === current)
            ? current
            : String(data.clinics[0]?.id || ""),
        );
      })
      .catch((err) => {
        if (active) {
          setError(errorText(err));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!clinicId) return;
    let active = true;
    const current = ++generation.current;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const response = await api.get(`/clinic-team/${clinicId}/`, {
          params: { page, search, status },
        });
        if (active && current === generation.current) setTeam(response.data);
        if (index?.can_manage_managers) {
          const response = await api.get("/clinic-managers/", {
            params: { clinic: clinicId },
          });
          if (active && current === generation.current)
            setManagers(response.data);
        }
      } catch (err) {
        if (active && current === generation.current) setError(errorText(err));
      } finally {
        if (active && current === generation.current) setLoading(false);
      }
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [clinicId, page, search, status, refresh, index?.can_manage_managers]);

  const reload = () => {
    setPage(1);
    setRefresh((value) => value + 1);
  };
  const openInvite = (manager = false) => {
    setManagerInvite(manager);
    setInvite({
      ...emptyInvite,
      billing_mode: clinic?.company_coverage_available
        ? "clinic"
        : "individual",
    });
    setFormError("");
  };
  const submitInvite = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");
    try {
      if (managerInvite) {
        await api.post("/clinic-managers/", {
          first_name: invite.first_name,
          last_name: invite.last_name,
          email: invite.email,
          clinic_id: Number(clinicId),
        });
      } else {
        await api.post(`/clinic-team/${clinicId}/`, invite);
      }
      setInvite(null);
      setNotice(
        managerInvite
          ? "Manager access saved. New accounts receive an activation email; existing accounts keep their login."
          : "Invitation sent. The therapist can set their password using the email link.",
      );
      reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  const saveSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFormError("");
    try {
      await api.patch(`/clinic-team/${clinicId}/${edit.id}/`, {
        ...(edit.can_update ? { share_clients: edit.share_clients,
          ...(edit.billing_mode ? { billing_mode: edit.billing_mode } : {}) } : {}),
        ...(edit.can_manage_role ? { is_clinic_manager: Boolean(edit.is_clinic_manager) } : {}),
      });
      setEdit(null);
      setNotice("Team settings saved.");
      reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  const confirmAction = async () => {
    setSaving(true);
    setFormError("");
    try {
      if (confirmation.manager)
        await api.delete(`/clinic-managers/${confirmation.id}/`);
      else
        await api.patch(`/clinic-team/${clinicId}/${confirmation.id}/`, {
          is_active: !confirmation.is_active,
        });
      setConfirmation(null);
      setNotice("Access updated.");
      reload();
    } catch (err) {
      setFormError(errorText(err));
    } finally {
      setSaving(false);
    }
  };
  const billingFields = (value, setValue) => (
    <>
      <Form.Group className="mb-3" controlId="team-billing">
        <Form.Label>Subscription paid by</Form.Label>
        <Form.Select
          value={value.billing_mode || ""}
          onChange={(e) => setValue({ ...value, billing_mode: e.target.value })}
        >
          {!value.billing_mode && <option value="">Not recorded</option>}
          <option
            value="clinic"
            disabled={
              !clinic?.company_coverage_available &&
              value.billing_mode !== "clinic"
            }
          >
            Company
          </option>
          <option value="individual">Therapist</option>
        </Form.Select>
        <Form.Text>
          {edit
            ? "Changing from company to therapist payment pauses access until an individual subscription is arranged with Plasticise."
            : "Therapist-funded invitations start a 14-day trial. Company-funded invitations use the clinic's existing subscription and dates."}
        </Form.Text>
      </Form.Group>
      <Form.Check
        id="team-share-clients"
        type="switch" role="switch"
        className="admin-switch mb-3"
        label="Share this clinic's client list with this therapist"
        checked={value.share_clients}
        onChange={(e) =>
          setValue({ ...value, share_clients: e.target.checked })
        }
      />
      <p className="small text-muted">
        When off, new clients stay in the therapist's private workspace;
        assigned clinic clients remain available. Turning sharing on includes
        the clinic list and future clients. Existing private records require
        Plasticise to transfer them.
      </p>
    </>
  );

  return (
    <Container className="py-4 mb-5" style={{ maxWidth: 1200 }}>
      <div className="d-flex flex-wrap gap-3 justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3">Clinic team</h1>
          <p className="text-muted mb-0">
            {clinic?.can_invite_therapists === false
              ? "Manage your practice."
              : "Manage invitations, staff access and client sharing."}
          </p>
        </div>
        {clinic && (
          <div className="d-flex flex-wrap gap-2">
            {clinic.can_invite_therapists && (
              <Button onClick={() => openInvite()}>Invite a therapist</Button>
            )}
            {index?.can_manage_managers && (
              <Button
                variant="outline-primary"
                onClick={() => openInvite(true)}
              >
                Add clinic manager
              </Button>
            )}
          </div>
        )}
      </div>
      {error && (
        <Alert variant="danger">
          {error}{" "}
          <Button variant="link" onClick={reload}>
            Retry
          </Button>
        </Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {index && !index.clinics.length && (
        <Alert variant="info">No clinics are assigned to your account.</Alert>
      )}
      {clinic && (
        <>
          <Form.Group controlId="team-clinic" className="mb-3">
            <Form.Label>Clinic</Form.Label>
            <Form.Select
              value={clinicId}
              onChange={(e) => {
                setClinicId(e.target.value);
                setPage(1);
                setSearch("");
                setStatus("");
                setNotice("");
              }}
            >
              {index.clinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Card className="mb-3">
            <Card.Body>
              <strong>
                Company subscription: {clinic.billing_state || "Not recorded"}
              </strong>
              <div className="small mt-1">
                Start: {dateText(clinic.start_date)} · Trial end:{" "}
                {dateText(clinic.trial_end_date)}
              </div>
              <div className="small text-muted mt-1">
                Billing and trial dates are managed by Plasticise. Each
                therapist's payment arrangement is shown below.
              </div>
            </Card.Body>
          </Card>
          <Row className="g-2 mb-3">
            <Col xs={12} md={8}>
              <Form.Control
                aria-label="Search therapists"
                placeholder="Search by name or email"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                aria-label="Filter staff status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All staff</option>
                <option value="active">Active accounts</option>
                <option value="inactive">Inactive accounts</option>
              </Form.Select>
            </Col>
          </Row>
        </>
      )}
      {loading ? (
        <div className="py-5 text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading team</span>
          </Spinner>
        </div>
      ) : (
        !error &&
        clinic && (
          <>
            <p className="text-muted">
              {team.count} therapist{team.count === 1 ? "" : "s"}
            </p>
            {!team.results.length && (
              <Alert variant="info">
                {clinic.can_invite_therapists
                  ? "No therapists match this view. Invite a therapist to get started."
                  : "No therapists match this view."}
              </Alert>
            )}
            <Row className="g-3">
              {team.results.map((person) => (
                <Col xs={12} lg={6} key={person.id}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="d-flex flex-wrap gap-2 align-items-start justify-content-between">
                        <h2 className="h5 mb-1">
                          {person.first_name} {person.last_name}
                          {person.is_clinic_manager && <span className="badge ms-2" style={{ backgroundColor: '#ec2873', fontSize: '0.7em' }}>Clinic manager</span>}
                        </h2>
                        <Badge
                          bg={
                            person.is_active
                              ? person.access_paused
                                ? "warning"
                                : "success"
                              : "secondary"
                          }
                        >
                          {!person.is_active
                            ? "Inactive"
                            : person.access_paused
                              ? "Subscription paused"
                              : person.invitation_pending
                                ? "Invited"
                                : "Active"}
                        </Badge>
                      </div>
                      <div className="text-break">{person.email}</div>
                      <p className="text-muted small">{person.discipline}</p>
                      <dl className="row small mb-2">
                        <dt className="col-6">Added</dt>
                        <dd className="col-6">{dateText(person.joined_at)}</dd>
                        <dt className="col-6">Deactivated</dt>
                        <dd className="col-6">
                          {dateText(person.deactivated_at)}
                        </dd>
                        <dt className="col-6">Paid by</dt>
                        <dd className="col-6">
                          {person.billing_mode === "clinic"
                            ? "Company"
                            : person.billing_mode === "individual"
                              ? "Therapist"
                              : "Not recorded"}
                        </dd>
                        <dt className="col-6">Subscription</dt>
                        <dd className="col-6">
                          {person.billing_state || "Not recorded"}
                        </dd>
                        <dt className="col-6">Trial start / end</dt>
                        <dd className="col-6">
                          {dateText(person.start_date)} /{" "}
                          {dateText(person.trial_end_date)}
                        </dd>
                        <dt className="col-6">Clinic client list</dt>
                        <dd className="col-6">
                          {person.share_clients
                            ? "Shared"
                            : "Private / assigned clients"}
                        </dd>
                      </dl>
                      {(person.can_update || person.can_manage_role) && (
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => {
                              setEdit({ ...person });
                              setFormError("");
                            }}
                          >
                            Team settings
                          </Button>
                          {person.can_update && <Button
                            size="sm"
                            variant={
                              person.is_active
                                ? "outline-danger"
                                : "outline-primary"
                            }
                            disabled={
                              !person.is_active && !person.can_reactivate
                            }
                            onClick={() => {
                              setConfirmation(person);
                              setFormError("");
                            }}
                          >
                            {person.is_active ? "Deactivate" : "Reactivate"}
                          </Button>}
                        </div>
                      )}
                      {!person.can_update && (
                        <p className="small text-muted mb-0">
                          Account activation and billing changes for protected accounts are managed by Plasticise.
                        </p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-flex justify-content-between align-items-center my-3">
              <Button
                variant="outline-secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span>
                Page {page} of {Math.max(1, Math.ceil(team.count / 25))}
              </span>
              <Button
                variant="outline-secondary"
                disabled={!team.next}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
            {index.can_manage_managers && (
              <Card className="mt-4">
                <Card.Body>
                  <h2 className="h5">Clinic managers</h2>
                  {managers.length ? (
                    managers.map((m) => (
                      <div
                        key={m.id}
                        className="d-flex flex-wrap gap-2 justify-content-between align-items-center py-2"
                      >
                        <span className="text-break">
                          {m.name} · {m.email}
                        </span>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => {
                            setConfirmation({ ...m, manager: true });
                            setFormError("");
                          }}
                        >
                          Revoke manager access
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="mb-0">No clinic managers assigned.</p>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )
      )}
      <Modal
        show={Boolean(invite)}
        onHide={() => !saving && setInvite(null)}
        centered
      >
        <Form onSubmit={submitInvite}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>
              {managerInvite ? "Add clinic manager" : "Invite a therapist"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Clinic: <strong>{clinic?.name}</strong>
            </p>
            {managerInvite && (
              <p className="small">
                Managers can manage staff for this clinic. They receive no
                clinical access unless they already have a therapist account.
              </p>
            )}
            {formError && <Alert variant="danger">{formError}</Alert>}
            {invite && (
              <fieldset disabled={saving}>
                {["first_name", "last_name", "email"].map((field) => (
                  <Form.Group
                    key={field}
                    className="mb-3"
                    controlId={`invite-${field}`}
                  >
                    <Form.Label>
                      {field === "first_name"
                        ? "First name"
                        : field === "last_name"
                          ? "Last name"
                          : "Email"}
                    </Form.Label>
                    <Form.Control
                      required
                      maxLength={field === "email" ? 254 : 30}
                      type={field === "email" ? "email" : "text"}
                      value={invite[field]}
                      onChange={(e) =>
                        setInvite({ ...invite, [field]: e.target.value })
                      }
                    />
                  </Form.Group>
                ))}
                {!managerInvite && (
                  <>
                    <Form.Group controlId="invite-discipline" className="mb-3">
                      <Form.Label>Profession</Form.Label>
                      <Form.Select
                        required
                        value={invite.discipline}
                        onChange={(e) =>
                          setInvite({ ...invite, discipline: e.target.value })
                        }
                      >
                        <option value="">Select profession</option>
                        {index?.disciplines.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    {billingFields(invite, setInvite)}
                  </>
                )}
              </fieldset>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => setInvite(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : managerInvite
                  ? "Save manager access"
                  : "Send invitation"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        show={Boolean(edit)}
        onHide={() => !saving && setEdit(null)}
        centered
      >
        <Form onSubmit={saveSettings}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>Team settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formError && <Alert variant="danger">{formError}</Alert>}
            {edit && (
              <fieldset disabled={saving}>
                <p>
                  {edit.first_name} {edit.last_name} · {clinic?.name}
                </p>
                {edit.can_manage_role && <Form.Check type="switch" role="switch" id="team-clinic-manager" className="admin-switch mb-3"
                  label="Clinic manager" checked={Boolean(edit.is_clinic_manager)}
                  onChange={(event) => setEdit({ ...edit, is_clinic_manager: event.target.checked })} />}
                {edit.can_update && billingFields(edit, setEdit)}
              </fieldset>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => setEdit(null)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        show={Boolean(confirmation)}
        onHide={() => !saving && setConfirmation(null)}
        centered
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>
            {confirmation?.manager
              ? "Revoke manager access?"
              : confirmation?.is_active
                ? "Deactivate therapist?"
                : "Reactivate therapist?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger">{formError}</Alert>}
          <p>{confirmation?.email}</p>
          <p>
            {confirmation?.manager
              ? "This removes management access to this clinic. Their other roles and clinic grants remain."
              : confirmation?.is_active
                ? "The therapist will lose account access. Their client records, programs and history are retained."
                : "Account access will be restored if their subscription permits it. Trial dates will stay the same."}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => setConfirmation(null)}
          >
            Cancel
          </Button>
          <Button variant="primary" disabled={saving} onClick={confirmAction}>
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
